import React, { useState, useEffect, useRef } from "react";
import {
  IconButton,
  Badge,
  Menu,
  List,
  ListItem,
  ListItemText,
  Typography,
  Divider,
  Button,
  Box,
  Tabs,
  Tab,
  Fade,
} from "@mui/material";
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { useTranslation } from "react-i18next";
import { getNoti, read } from "../../actions/notification";
import { getSocketAdmin, getSocketUser } from "../../services/socket";
import useLocalStorage from "../../hook/useLocalStorage";

function NotificationDropdown(props) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [notifications, setNotifications] = useState({
    all: [],
    cases: [],
    others: []
  });
  const [unreadCount, setUnreadCount] = useState({
    all: 0,
    cases: 0,
    others: 0
  });
  const [visibleNotifications, setVisibleNotifications] = useState({
    all: 4,
    cases: 4,
    others: 4
  });
  const [expandedNotifications, setExpandedNotifications] = useState({});
  const [heights, setHeights] = useState({});
  const [activeTab, setActiveTab] = useState(0);
  const [markingRead, setMarkingRead] = useState(false);
  const { t, i18n } = useTranslation("global");
  const language = i18n.language || 'es';
  const contentRefs = useRef({});
  const [notificacionesSAX] = useLocalStorage('NotificacionesSAX', true);
  const [storageUpdated, setStorageUpdated] = useState(false);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'NotificacionesSAX') {
        setStorageUpdated(prev => !prev);
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  useEffect(() => {
    if (!notificacionesSAX && activeTab !== 2) {
      setActiveTab(2);
    }
  }, [notificacionesSAX]);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = async () => {
    await markVisibleNotificationsAsRead();
    setAnchorEl(null);
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const fetchNotifications = async () => {
    try {
      const response = await getNoti(props.infoUser.id);
      const { caseNotifications, userNotifications } = response.data;

      const processedCaseNotifications = notificacionesSAX 
        ? caseNotifications.map(cn => ({
            ...cn.notification,
            userNotificationId: cn.id,
            is_read: cn.is_read,
            is_case: true
          }))
        : [];

      const processedUserNotifications = userNotifications.map(un => ({
        ...un.notification,
        userNotificationId: un.id,
        is_read: un.is_read,
        is_case: false
      }));

      const allNotifications = [
        ...processedCaseNotifications,
        ...processedUserNotifications
      ].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

      setNotifications({
        all: allNotifications,
        cases: processedCaseNotifications,
        others: processedUserNotifications
      });

      setUnreadCount({
        all: allNotifications.filter(n => !n.is_read).length,
        cases: processedCaseNotifications.filter(n => {
          if (n.is_read) return false;
          const localizedDesc = language === 'en' ? n.description_en : n.description_es;
          const status = getStatusInfo(localizedDesc);
          return status.type === 'error';
        }).length,
        others: processedUserNotifications.filter(n => !n.is_read).length
      });
    } catch (error) {
      console.error("Error fetching notifications:", error);
      setNotifications({ all: [], cases: [], others: [] });
      setUnreadCount({ all: 0, cases: 0, others: 0 });
    }
  };

  const markVisibleNotificationsAsRead = async () => {
    if (markingRead) return;
    
    setMarkingRead(true);
    try {
      const currentTab = activeTab === 0 ? 'all' : activeTab === 1 ? 'cases' : 'others';
      const visibleUnread = notifications[currentTab]
        .slice(0, visibleNotifications[currentTab])
        .filter(n => !n.is_read && n.userNotificationId);

      if (visibleUnread.length === 0) return;

      await new Promise(resolve => setTimeout(resolve, 200));
      
      await Promise.all(
        visibleUnread.map(notification => read(notification.userNotificationId))
      );

      await fetchNotifications();
    } catch (error) {
      console.error("Error marking notifications as read:", error);
    } finally {
      setMarkingRead(false);
    }
  };

  const markSingleNotificationAsRead = async (notification) => {
    if (notification.is_read || !notification.userNotificationId || markingRead) return;
    
    setMarkingRead(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      
      await read(notification.userNotificationId);
      await fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as read:", error);
    } finally {
      setMarkingRead(false);
    }
  };

  const getLocalizedNotification = (notification) => {
    const isEnglish = language === 'en';
    return {
      title: isEnglish ? notification.title_en : notification.title_es,
      description: isEnglish ? notification.description_en : notification.description_es,
    };
  };

  const formatNotificationTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString(language, { hour: '2-digit', minute: '2-digit' });
  };

  const handleExpandNotification = (id) => {
    setExpandedNotifications(prev => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const getDynamicFontSize = (text) => {
    const length = text?.length || 0;
    if (length > 100) return '0.7rem';
    if (length > 60) return '0.8rem';
    if (length > 40) return '0.9rem';
    return '1rem';
  };

  useEffect(() => {
    const socketAdmin = getSocketAdmin();
    const socketUser = getSocketUser();
  
    const handleNewNotification = (data) => {
      if (!notificacionesSAX && data?.is_case) return;
      fetchNotifications();
    };
  
    socketAdmin?.on("notification", handleNewNotification);
    socketUser?.on("notification", handleNewNotification);
  
    return () => {
      socketAdmin?.off("notification", handleNewNotification);
      socketUser?.off("notification", handleNewNotification);
    };
  }, [notificacionesSAX]);

  useEffect(() => {
    fetchNotifications();
  }, [props.infoUser.id, language, notificacionesSAX, storageUpdated]);

  const loadMoreNotifications = () => {
    const currentTab = activeTab === 0 ? 'all' : activeTab === 1 ? 'cases' : 'others';
    setVisibleNotifications(prev => ({
      ...prev,
      [currentTab]: prev[currentTab] + 6
    }));
  };

  const getStatusInfo = (description) => {
    const desc = description.toLowerCase();
    const isEnglish = language === 'en';
    
    if (desc.includes('procesando') || (!isEnglish && desc.includes('proceso'))) {
      return {
        text: t('Notification.Procesando'),
        color: 'rgb(63, 0, 142)',
        type: 'processing'
      };
    }
    
    if (desc.includes('finalizado') || (!isEnglish && (desc.includes('completado') || desc.includes('terminado')))) {
      return {
        text: t('Notification.Finalizado'),
        color: 'rgb(0, 128, 0)',
        type: 'completed'
      };
    }
    
    if ((isEnglish && desc.includes('processing')) || desc.includes('in progress')) {
      return {
        text: t('Notification.Procesando'),
        color: 'rgb(63, 0, 142)',
        type: 'processing'
      };
    }
    
    if ((isEnglish && (desc.includes('completed') || desc.includes('finished')))) {
      return {
        text: t('Notification.Finalizado'),
        color: 'rgb(0, 128, 0)',
        type: 'completed'
      };
    }
    
    if (desc.includes('error') || desc.includes('failed')) {
      return {
        text: t('Notification.Error'),
        color: 'rgb(255, 0, 0)',
        type: 'error'
      };
    }
    
    return {
      text: t('Notification.Procesando'),
      color: 'rgb(63, 0, 142)',
      type: 'processing'
    };
  };

  const renderCaseNotification = (notification) => {
    const localizedNotification = getLocalizedNotification(notification);
    const timeString = formatNotificationTime(notification.created_at);
    const statusInfo = getStatusInfo(localizedNotification.description);
    const titleFontSize = getDynamicFontSize(localizedNotification.title);
    
    return (
      <Fade in={true} key={notification.id}>
        <ListItem
          sx={{
            backgroundColor: notification.is_read ? "inherit" : "rgba(255, 106, 19, 0.1)",
            "&:hover": { backgroundColor: "#f5f5f5" },
            padding: '12px 16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            transition: 'background-color 0.3s ease',
            flexWrap: 'wrap'
          }}
          onClick={() => markSingleNotificationAsRead(notification)}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            {!notification.is_read && (
              <Box 
                component="span" 
                sx={{ 
                  display: 'inline-block',
                  width: '8px',
                  height: '8px',
                  backgroundColor: 'rgb(255, 106, 19)',
                  borderRadius: '50%',
                  marginRight: '8px'
                }} 
              />
            )}
            <Box sx={{ minWidth: 0 }}>
              <Typography 
                variant="body1" 
                sx={{ 
                  color: 'grey', 
                  fontWeight: 'bold',
                  marginBottom: '4px',
                  fontSize: titleFontSize,
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden'
                }}
              >
                {localizedNotification.title}
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: 'grey',
                  fontSize: '0.75rem'
                }}
              >
                {timeString}
              </Typography>
            </Box>
          </Box>
          
          <Box
            sx={{
              backgroundColor: statusInfo.color,
              color: 'white',
              borderRadius: '12px',
              minWidth: '100px',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '0.8rem',
              fontWeight: 'bold',
              transition: 'opacity 0.3s ease',
              opacity: markingRead ? 0.7 : 1,
              marginLeft: '8px',
              flexShrink: 0
            }}
          >
            {statusInfo.text}
          </Box>
        </ListItem>
      </Fade>
    );
  };

  const renderNormalNotification = (notification) => {
    const localizedNotification = getLocalizedNotification(notification);
    const timeString = formatNotificationTime(notification.created_at);
    const isExpanded = expandedNotifications[notification.id] || false;
    const titleFontSize = getDynamicFontSize(localizedNotification.title);

    return (
      <Fade in={true} key={notification.id}>
        <div>
          <ListItem
            button
            onClick={(e) => {
              handleExpandNotification(notification.id);
              markSingleNotificationAsRead(notification);
            }}
            sx={{
              backgroundColor: notification.is_read ? "inherit" : "rgba(255, 106, 19, 0.1)",
              "&:hover": { backgroundColor: "#f5f5f5" },
              transition: 'background-color 0.3s ease',
              flexWrap: 'wrap'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
              {!notification.is_read && (
                <Box 
                  component="span" 
                  sx={{ 
                    display: 'inline-block',
                    width: '8px',
                    height: '8px',
                    backgroundColor: 'rgb(255, 106, 19)',
                    borderRadius: '50%',
                    marginRight: '8px'
                  }} 
                />
              )}
              <ListItemText
                primary={
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: 'grey', 
                      fontWeight: 'bold',
                      fontSize: titleFontSize,
                      wordBreak: 'break-word',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}
                  >
                    {localizedNotification.title}
                  </Typography>
                }
                secondary={
                  <Typography variant="caption" sx={{ color: 'grey', fontSize: '0.75rem' }}>
                    {timeString}
                  </Typography>
                }
                sx={{ minWidth: 0 }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                handleExpandNotification(notification.id);
                markSingleNotificationAsRead(notification);
              }}
              sx={{
                backgroundColor: 'rgb(63, 0, 142)',
                color: 'white',
                borderRadius: '50%',
                padding: '8px',
                '&:hover': { backgroundColor: 'rgb(63, 0, 142)' },
                transition: 'opacity 0.3s ease',
                opacity: markingRead ? 0.7 : 1,
                flexShrink: 0
              }}
            >
              {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </ListItem>
          <Box
            ref={el => contentRefs.current[notification.id] = el}
            sx={{
              overflow: 'hidden',
              transition: 'max-height 0.3s ease, padding 0.3s ease',
              maxHeight: isExpanded ? `${heights[notification.id] || 500}px` : '0px',
              padding: isExpanded ? '0 16px 16px 16px' : '0 16px 0px 16px',
              marginLeft: '16px',
              width: 'calc(100% - 32px)'
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: 'grey',
                wordBreak: 'break-word'
              }}
            >
              {timeString} - {localizedNotification.description}
            </Typography>
          </Box>
        </div>
      </Fade>
    );
  };

  useEffect(() => {
    const newHeights = {};
    Object.keys(contentRefs.current).forEach(key => {
      if (contentRefs.current[key]) {
        newHeights[key] = contentRefs.current[key].scrollHeight;
      }
    });
    setHeights(newHeights);
  }, [notifications, expandedNotifications]);

  const renderNotificationItem = (notification) => {
    return notification.is_case 
      ? renderCaseNotification(notification)
      : renderNormalNotification(notification);
  };

  const currentTabNotifications = activeTab === 0 
    ? notifications.all.slice(0, visibleNotifications.all)
    : activeTab === 1 
      ? notifications.cases.slice(0, visibleNotifications.cases)
      : notifications.others.slice(0, visibleNotifications.others);

  const currentTabHasMore = activeTab === 0 
    ? notifications.all.length > visibleNotifications.all
    : activeTab === 1 
      ? notifications.cases.length > visibleNotifications.cases
      : notifications.others.length > visibleNotifications.others;

  return (
    <div>
      <IconButton
        aria-label="notifications"
        color="inherit"
        onClick={handleMenuOpen}
      >
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <Badge 
            badgeContent={unreadCount.all} 
            sx={{ 
              position: 'absolute',
              top: '-5px',
              right: '15px',
              '& .MuiBadge-badge': {
                backgroundColor: 'rgb(255, 106, 19)',
                color: 'white',
                borderRadius: '4px',
                padding: '0 4px',
                minWidth: '20px',
                height: '20px',
                fontSize: '0.75rem',
              }
            }}
          />
          <NotificationsActiveIcon sx={{ fontSize: '2rem', color: 'rgb(63, 0, 142)' }} />
        </Box>
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          style: {
            width: "min(435px, 90vw)", 
            maxHeight: "70vh",
            borderRadius: '12px',
            position: 'relative',
            zIndex: 1300,
            backgroundColor: 'white',
            display: 'flex',
            flexDirection: 'column',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: '-10px',
              right: '14px',
              width: 0,
              height: 0,
              borderLeft: '10px solid transparent',
              borderRight: '10px solid transparent',
              borderBottom: '10px solid white',
            },
          },
        }}
      >
        <Box sx={{ overflow: 'hidden', flexShrink: 0 }}>
        {notificacionesSAX ? (
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              '& .MuiTabs-indicator': {
                backgroundColor: 'rgb(63, 0, 142)',
                height: '3px'
              },
              '& .MuiTab-root': {
                minWidth: 0,
                padding: '12px 8px',
                margin: '0 4px',
                fontSize: '0.8rem',
                color: 'rgb(63, 0, 142)',
                fontWeight: 'bold',
                '&.Mui-selected': {
                  color: 'rgb(63, 0, 142)',
                }
              },
              paddingX: '8px'
            }}
          >
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    sx={{ 
                      color: 'inherit',
                      fontWeight: 'inherit',
                      fontSize: '0.8rem'
                    }}
                  >
                    {t('Notification.Todas')}
                  </Typography>
                  {unreadCount.all > 0 && (
                    <Box 
                      sx={{ 
                        ml: 1,
                        backgroundColor: 'rgb(255, 106, 19)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      {unreadCount.all}
                    </Box>
                  )}
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    sx={{ 
                      color: 'inherit',
                      fontWeight: 'inherit',
                      fontSize: '0.8rem'
                    }}
                  >
                    {t('Notification.Casos')}
                  </Typography>
                  {unreadCount.cases > 0 && (
                    <Box 
                      sx={{ 
                        ml: 1,
                        backgroundColor: 'rgb(255, 106, 19)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      {unreadCount.cases}
                    </Box>
                  )}
                </Box>
              }
            />
            <Tab 
              label={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography 
                    sx={{ 
                      color: 'inherit',
                      fontWeight: 'inherit',
                      fontSize: '0.8rem'
                    }}
                  >
                    {t('Notification.Notificaciones')}
                  </Typography>
                  {unreadCount.others > 0 && (
                    <Box 
                      sx={{ 
                        ml: 1,
                        backgroundColor: 'rgb(255, 106, 19)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '20px',
                        height: '20px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '0.75rem'
                      }}
                    >
                      {unreadCount.others}
                    </Box>
                  )}
                </Box>
              }
            />
          </Tabs>
        ) : (
          <Box sx={{ padding: '12px 16px', textAlign: 'center' }}>
            <Typography variant="h6" sx={{ color: 'rgb(63, 0, 142)', fontWeight: 'bold' }}>
              {t('Notification.Notificaciones')}
            </Typography>
          </Box>
        )}
          <Divider />
        </Box>
        
        <Box sx={{ overflow: 'auto', flex: 1 }}>
          <List sx={{ overflow: 'visible' }}>
            {currentTabNotifications.length > 0 ? (
              <>
                {currentTabNotifications.map(renderNotificationItem)}
                {currentTabHasMore && (
                  <ListItem>
                    <Button
                      fullWidth
                      onClick={loadMoreNotifications}
                      sx={{
                        backgroundColor: 'white',
                        color: 'rgb(63, 0, 142)',
                        fontWeight: 'bold',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0',
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        },
                      }}
                    >
                      {t('Notification.VerMas')}
                    </Button>
                  </ListItem>
                )}
              </>
            ) : (
              <ListItem>
                <ListItemText 
                  primary={
                    <Typography 
                      sx={{ 
                        textAlign: 'center',
                        color: 'grey',
                        fontStyle: 'italic'
                      }}
                    >
                      {t('Notification.NoHayNotificaciones')}
                    </Typography>
                  } 
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Menu>
    </div>
  );
}

export default NotificationDropdown;